import styled from '@emotion/styled'
import { Image } from 'app/components/Common/Image'
import { FlexBox } from 'app/components/Layout/FlexBox'
import { styles } from 'app/theme'
import { AnimatePresence, anticipate, circOut, motion } from 'framer-motion'
import React, { memo, useEffect, useState } from 'react'

import { MenuItem, Props as MenuItemProps } from './MenuItem'

export interface Props {
  menuItems: MenuItemProps[]
  modalStatus?: boolean
  onClick?: any
  pageID?: string
  siteName?: string
}

export const Navigation = memo(function Navigation({
  menuItems,
  modalStatus,
  pageID,
  siteName,
}: Props) {
  if (!menuItems) {
    return null
  }

  const bgVariants = {
    visible: (i: any) => ({
      height: '100%',
      transition: {
        bounce: 0,
        delay: i * 0.07 + 0.1,
        duration: 1,
        ease: anticipate,
        type: 'spring',
      },
    }),
    hidden: (i: any) => ({
      height: 0,
      transition: {
        bounce: 0,
        delay: i * 0.07 + 0.4,
        duration: 1,
        ease: anticipate,
        type: 'spring',
      },
    }),
  }

  const variants = {
    visible: (i: any) => ({
      opacity: 1,
      y: 0,
      transition: {
        bounce: 0,
        delay: i * 0.2 + 0.3,
        duration: 1,
        ease: circOut,
        type: 'spring',
      },
    }),
    hidden: {
      opacity: 0,
      y: 30,
      transition: {
        bounce: 0,
        delay: 0,
        duration: 1,
        ease: circOut,
        type: 'spring',
      },
    },
  }

  const [hoverImage, setHoverImage] = useState('')
  const [currentIndex, setCurrentIndex] = useState<number>()

  const [submenuVisibility, setSubmenuVisibility] = useState<number>()

  useEffect(() => {
    const currentMenuItem = menuItems
      .map((c) => {
        if (typeof window === 'undefined') return

        return c.childs?.find((i) => window.location.pathname === i.url)
      })
      .findIndex((i) => i !== undefined)

    setCurrentIndex(currentMenuItem)
  }, [])

  const onHoverLink = (title: string) => {
    setHoverImage(title)
  }

  const onHoverOutLink = () => {
    setHoverImage('')
  }

  let menuImages: any = []

  menuItems.map((item) => {
    if (item.childs) {
      item.childs.map((child) => {
        let image: any = {
          title: '',
          image: null,
        }

        image.alt = child.title
        image.id = child.id
        image.title = child.title
        image.image = child.menuImage

        menuImages.push(image)
      })
    } else {
      let image: any = {
        title: '',
        image: null,
      }

      image.alt = item.title
      image.id = item.id
      image.title = item.title
      image.image = item.menuImage

      menuImages.push(image)
    }
  })

  return (
    <Container
      className={modalStatus ? 'open' : undefined}
      onClick={(e) => e.stopPropagation()}
      dial={4}
    >
      <Wrapper>
        <LeftSide>
          <Nav>
            <NavList>
              <AnimatePresence initial={false}>
                {menuItems.map((item, index) => (
                  <NavLi
                    animate={modalStatus ? 'visible' : 'hidden'}
                    custom={index}
                    key={index}
                    initial="hidden"
                    variants={variants}
                  >
                    <MenuItem
                      {...item}
                      modalStatus={modalStatus}
                      onClick={() => {
                        if (submenuVisibility === index) {
                          setSubmenuVisibility(-1)
                        } else {
                          if (currentIndex === index) {
                            setSubmenuVisibility(-1)
                            setCurrentIndex(-1)
                          } else {
                            setSubmenuVisibility(index)
                            setCurrentIndex(-1)
                          }
                        }
                      }}
                      onHoverLink={onHoverLink}
                      onHoverLinkOut={onHoverOutLink}
                      submenuVisibility={
                        submenuVisibility === index || currentIndex === index
                      }
                    />
                  </NavLi>
                ))}
              </AnimatePresence>
            </NavList>
          </Nav>
        </LeftSide>

        {modalStatus ? (
          <Images className={modalStatus ? 'open' : undefined}>
            {menuImages.map((item: any, index: any) => {
              if (!item.image) return

              return (
                <Img
                  alt={item.alt}
                  className={
                    pageID === item.id && !hoverImage
                      ? 'default'
                      : hoverImage === item.title
                        ? 'active'
                        : undefined
                  }
                  key={index}
                  {...item.image}
                />
              )
            })}
          </Images>
        ) : null}
      </Wrapper>
    </Container>
  )
})

const Container = styled(FlexBox)`
  width: 100%;
  height: 100%;
  background: ${({ theme }) => theme.colors.variants.neutralLight3};
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;

  &.open {
    transition: 0.3s ease-in-out;
    &:before {
      left: 0;
      width: 100%;
      transition: 0.5s ease-in-out;
    }
  }

  > svg {
    position: absolute;
    top: 1.375rem;
    left: 50%;
    transform: translateX(-50%);
    width: 8.0625rem;
    height: 2.25rem;
    fill: ${({ theme }) => theme.colors.variants.neutralDark2};
  }

  opacity: 0;
  transform: translateY(-100%);

  > div:last-of-type {
    opacity: 0;
    transition: 0.6s ease-in-out;
  }

  &.open {
    transition: 0.6s ease-in-out;
    opacity: 1;
    transform: none;

    > div:last-of-type {
      opacity: 1;
      transition: 0.6s 0.6s ease-in-out;
    }
  }
`

const Wrapper = styled(FlexBox)`
  width: 100%;
  height: 100%;
  overflow-y: auto;
  padding-top: 9.5rem;
  -webkit-overflow-scrolling: auto;

  @media (max-width: 1199px) {
    display: block;
    padding-top: 0.625rem;
  }
`

const LeftSide = styled.div`
  width: 55.972vw;
  height: 100%;
  position: relative;

  @media (max-width: 1199px) {
    width: 100%;
    overflow-y: auto;
    overflow-x: hidden;
    transform: none;
  }

  .item-contacts {
    position: absolute;
    bottom: 3.75rem;
    right: 0;
    left: 5.833vw;

    @media (max-width: 1439px) {
      display: none;
    }
  }
`

const Nav = styled.nav`
  display: flex;
  justify-content: space-between;
  position: absolute;
  top: -3rem;
  right: 0;
  left: 5.833vw;
  padding-bottom: 3.75rem;

  @media (max-width: 1199px) {
    flex-direction: column;
    top: 5rem;
    left: 0;
    padding-left: 1.5rem;
  }
  @media (max-width: 767px) {
    padding-bottom: 1.875rem;
    top: 4.375rem;
  }
`

const NavList = styled.ul`
  min-width: 12.5rem;

  @media (max-width: 1199px) {
    width: 100%;
  }
`

const NavLi = styled(motion.li)`
  margin-top: 0.5rem;

  .item-book-cta {
    margin-top: 0.625rem;
    display: block;
    transition: 0.2s cubic-bezier(0.25, 1, 0.5, 1);

    &:hover {
      transition: 0.2s cubic-bezier(0.25, 1, 0.5, 1);
      opacity: 0.6;
    }
  }

  @media (max-width: 1199px) {
    margin-top: 1rem;

    .item-book-cta {
      display: none;
    }
  }
`

const Images = styled.div`
  width: 34.167vw;
  height: calc(100% - 13.334vw);
  position: fixed;
  top: 6.667vw;
  right: 6.667vw;
  bottom: 6.667vw;
  filter: blur(1.25rem);
  opacity: 0;
  transition:
    opacity 0.5s 1s,
    transform 0.5s 1s,
    filter 0.5s 1s;
  z-index: 2;
  &.open {
    filter: blur(0);
    opacity: 1;
  }

  @media (max-width: 1199px) {
    display: none;
  }
`

const Img = styled(Image)`
  img {
    ${styles.img}

    filter: blur(1.25rem);
    opacity: 0 !important;
    transition:
      opacity 0.5s,
      transform 0.5s,
      filter 0.5s;
  }

  &.active,
  &.default {
    img {
      filter: blur(0);
      opacity: 1 !important;
    }
  }
`
